import React from 'react'
import { Currency, Price } from '@uniswap/sdk-core'
import StepCounter from 'components/InputStepCounter/InputStepCounter'
import { AutoRow, RowBetween } from 'components/Row'
import { useActiveWeb3React } from 'hooks'
import { wrappedCurrency } from 'utils/wrappedCurrency'

import { nearestUsableTick, TickMath } from '@uniswap/v3-sdk'
import { BigNumber } from 'ethers'
import { formatUnits } from 'ethers/lib/utils'
import styled from 'styled-components'
import { PresetPriceRange } from 'pages/AddLiquidity'
import { AutoColumn } from 'components/Column'
import { ButtonOutlined, ButtonRadioChecked } from 'components/Button'
import { TYPE } from 'theme'
import Tag from 'components/Tag'
import TagButton from 'components/TagButton'

const ToggleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${({ theme }) => theme.bg1};
  border-radius: 16px;
  padding: 6px;
  width: 100%;
  cursor: pointer;
  position: relative;
`

const ToggleOption = styled.div<{ active: boolean }>`
  flex: 1;
  text-align: center;
  padding: 10px 12px;
  font-size: 14px;
  font-weight: ${({ active }) => (active ? '600' : '400')};
  color: ${({ theme, active }) => (active ? theme.text5 : theme.text4)};
  background: ${({ theme, active }) => (active ? theme.primary1 : 'transparent')};
  border-radius: 12px;
  transition: all 0.2s ease-in-out;

  &:hover {
    color: ${({ theme, active }) => (active ? theme.text5 : theme.text3)};
  }
`

const PresetOptions = styled.div`
  margin: auto;
  display: grid;
  grid-template-columns: repeat(2, 1fr); /* 2 items per row */
  gap: 12px;
  width: 100%;
`
const ResponsiveText = styled(TYPE.label)`
  ${({ theme }) => theme.mediaWidth.upToSmall`
    font-size: 12px;
  `};
`

const FadeContainer = styled.div`
  opacity: 0;
  max-height: 0;
  overflow: hidden;
  transition: opacity 0.3s ease-in-out, max-height 0.3s ease-in-out;
  max-width: 500px;

  &.open {
    opacity: 1;
    max-height: 500px;
  }
`

const presetLabels: Record<string, string> = {
  passive: 'Passive',
  wide: 'Wide',
  narrow: 'Narrow',
  aggressive: 'Aggressive',
  maxRange: 'Max Range',
}

const presetPercentages: Record<string, string> = {
  passive: '± 50%',
  wide: '± 25%',
  narrow: '± 5%',
  aggressive: '± 1%',
}

const presetDescriptions: Record<string, string> = {
  passive: 'Very wide range — low maintenance.',
  wide: 'Wide range — stable fee generation.',
  narrow: 'Tight range — balanced strategy.',
  aggressive: 'Focused range — high fee potential.',
  maxRange: 'Full range — minimal capital efficiency.',
}
// currencyA is the base token
export default function RangeSelector({
  priceLower,
  priceUpper,
  onLeftRangeInput,
  onRightRangeInput,
  getDecrementLower,
  getIncrementLower,
  getDecrementUpper,
  getIncrementUpper,
  currencyA,
  currencyB,
  feeAmount,
  setPresetPriceRange,
  presetPriceRange,
  price,
  triggerInfiniteRange,
  setTriggerInfiniteRange,
}: {
  priceLower?: Price
  priceUpper?: Price
  getDecrementLower: () => string
  getIncrementLower: () => string
  getDecrementUpper: () => string
  getIncrementUpper: () => string
  onLeftRangeInput: (typedValue: string) => void
  onRightRangeInput: (typedValue: string) => void
  currencyA?: Currency | null
  currencyB?: Currency | null
  feeAmount?: number
  setPresetPriceRange?: (presetMaxPriceRange: PresetPriceRange) => void
  presetPriceRange?: PresetPriceRange
  price?: any
  triggerInfiniteRange?: boolean
  setTriggerInfiniteRange?: (triggerInfiniteRange: boolean) => void
}) {
  const { chainId } = useActiveWeb3React()
  const [safeMinPrice, setSafeMinPrice] = React.useState('')
  const [safeMaxPrice, setSafeMaxPrice] = React.useState('')
  const [minDecimals, setMinDecimals] = React.useState(18)
  const [maxDecimals, setMaxDecimals] = React.useState(18)
  const [isCustomRange, setIsCustomRange] = React.useState(false)
  const [isOpen, setIsOpen] = React.useState(false)

  const tokenA = wrappedCurrency(currencyA ?? undefined, chainId)
  const tokenB = wrappedCurrency(currencyB ?? undefined, chainId)
  const isSorted = tokenA && tokenB && tokenA?.address !== tokenB?.address && tokenA.sortsBefore(tokenB)

  const leftPrice = isSorted ? priceLower : priceUpper?.invert()
  const rightPrice = isSorted ? priceUpper : priceLower?.invert()

  const handleInfiniteRange = React.useCallback(() => {
    if (!currencyA || !currencyB || !feeAmount) return

    const MAX_THRESHOLD = BigNumber.from('10').pow(36)

    const tickSpacing =
      feeAmount === 10000 ? 200 : feeAmount === 3000 ? 60 : feeAmount === 500 ? 10 : feeAmount === 100 ? 1 : 1

    const minTick = nearestUsableTick(TickMath.MIN_TICK, tickSpacing)
    const maxTick = nearestUsableTick(TickMath.MAX_TICK, tickSpacing)

    const sqrtRatioX96Min = TickMath.getSqrtRatioAtTick(minTick)
    const sqrtRatioX96Max = TickMath.getSqrtRatioAtTick(maxTick)

    const sqrtRatioX96MinBN = BigNumber.from(sqrtRatioX96Min.toString())
    const sqrtRatioX96MaxBN = BigNumber.from(sqrtRatioX96Max.toString())

    const currencyASorted = isSorted ? currencyA : currencyB
    const currencyBSorted = isSorted ? currencyB : currencyA

    const minDecimals = Math.min(currencyASorted?.decimals ?? 18, currencyBSorted?.decimals ?? 18)
    const maxDecimals = Math.max(currencyASorted?.decimals ?? 18, currencyBSorted?.decimals ?? 18)

    setMinDecimals(minDecimals)
    setMaxDecimals(maxDecimals)

    const minPrice = sqrtRatioX96MinBN.mul(sqrtRatioX96MinBN).div(BigNumber.from(2).pow(192))
    const maxPrice = sqrtRatioX96MaxBN.mul(sqrtRatioX96MaxBN).div(BigNumber.from(2).pow(192))

    const safeMinPrice = minPrice.isZero()
      ? formatUnits(BigNumber.from(1), minDecimals)
      : formatUnits(minPrice, minDecimals)

    const safeMaxPrice = maxPrice.gte(BigNumber.from('10').pow(18))
      ? formatUnits(MAX_THRESHOLD, maxDecimals)
      : formatUnits(maxPrice.mul(BigNumber.from(999)).div(BigNumber.from(1000)), maxDecimals)

    setSafeMinPrice(safeMinPrice)
    setSafeMaxPrice(safeMaxPrice)

    onLeftRangeInput(safeMinPrice)
    onRightRangeInput(safeMaxPrice)
  }, [currencyA, currencyB, feeAmount, isSorted, onLeftRangeInput, onRightRangeInput])

  const safeMinPriceNumber = parseFloat(safeMinPrice)
  const leftPriceNumber = parseFloat(leftPrice?.toSignificant(6) ?? '0')

  const safeMaxPriceNumber = parseFloat(safeMaxPrice)
  const rightPriceNumber = parseFloat(rightPrice?.toSignificant(6) ?? '0')

  const isMaxPriceRange =
    leftPriceNumber > 0 &&
    Math.abs(safeMinPriceNumber - leftPriceNumber) / leftPriceNumber <= 0.05 &&
    rightPriceNumber > 0 &&
    Math.abs(safeMaxPriceNumber - rightPriceNumber) / rightPriceNumber <= 0.05

  const resetPriceRange = () => {
    if (price) {
      const priceNumber = parseFloat(price)
      const lower = (priceNumber * 0.9).toFixed(6) // -10%
      const upper = (priceNumber * 1.1).toFixed(6) // +10%
      onLeftRangeInput(lower)
      onRightRangeInput(upper)
    } else {
      onLeftRangeInput('')
      onRightRangeInput('')
    }
  }
  React.useEffect(() => {
    handleInfiniteRange()
    if (setPresetPriceRange) {
      setPresetPriceRange('maxRange')
    }
  }, [handleInfiniteRange, setPresetPriceRange, tokenA, tokenB])

  React.useEffect(() => {
    if (triggerInfiniteRange === true) {
      handleInfiniteRange()
      if (setPresetPriceRange) {
        setPresetPriceRange('maxRange')
      }
      if (setTriggerInfiniteRange) {
        setTriggerInfiniteRange(false)
      }
    }
  }, [triggerInfiniteRange, setPresetPriceRange, setTriggerInfiniteRange, handleInfiniteRange])

  const applyPresetRange = (percent: number) => {
    if (!price) return

    const priceNumber = parseFloat(price)
    const lower = (priceNumber * (1 - percent)).toFixed(6)
    const upper = (priceNumber * (1 + percent)).toFixed(6)

    onLeftRangeInput(lower)
    onRightRangeInput(upper)
  }

  const applyPassiveRange = () => {
    applyPresetRange(0.5)
    if (setPresetPriceRange) {
      setPresetPriceRange('passive')
    }
    setIsOpen(false)
  }
  const applyWideRange = () => {
    applyPresetRange(0.25)
    if (setPresetPriceRange) {
      setPresetPriceRange('wide')
    }
    setIsOpen(false)
  }
  const applyNarrowRange = () => {
    applyPresetRange(0.05)
    if (setPresetPriceRange) {
      setPresetPriceRange('narrow')
    }
    setIsOpen(false)
  }
  const applyAggressiveRange = () => {
    applyPresetRange(0.01)
    if (setPresetPriceRange) {
      setPresetPriceRange('aggressive')
    }
    setIsOpen(false)
  }

  const applyMaxPriceRange = () => {
    setIsCustomRange(false)
    handleInfiniteRange()
    if (setPresetPriceRange) {
      setPresetPriceRange('maxRange')
    }
    setIsOpen(false)
  }

  return (
    <>
      <RowBetween>
        <ToggleWrapper>
          <ToggleOption active={!isCustomRange} onClick={() => applyMaxPriceRange()}>
            Preset Ranges
          </ToggleOption>
          <ToggleOption
            active={isCustomRange}
            onClick={() => {
              resetPriceRange()
              setIsCustomRange(true)
              setIsOpen(false)
              if (setPresetPriceRange) {
                setPresetPriceRange(null)
              }
            }}
          >
            Custom range
          </ToggleOption>
        </ToggleWrapper>
      </RowBetween>

      {presetPriceRange && !isCustomRange && (
        <>
          <AutoRow style={{ maxWidth: '500px' }}>
            <ButtonOutlined
              onClick={() => setIsOpen(!isOpen)}
              style={{ display: 'flex', justifyContent: 'space-between' }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <ResponsiveText style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {presetLabels[presetPriceRange]}{' '}
                  {presetPriceRange !== 'maxRange' && <Tag variant={1}>{presetPercentages[presetPriceRange]}</Tag>}
                </ResponsiveText>
                <TYPE.main fontWeight={400} fontSize="12px" textAlign="left">
                  {presetDescriptions[presetPriceRange]}
                </TYPE.main>
              </div>

              <TagButton onClick={() => setIsOpen(!isOpen)}>
                {isOpen ? (
                  <>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="icon icon-tabler icons-tabler-outline icon-tabler-chevron-up"
                    >
                      <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                      <path d="M6 15l6 -6l6 6" />
                    </svg>
                    Hide
                  </>
                ) : (
                  <>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="icon icon-tabler icons-tabler-outline icon-tabler-chevron-down"
                    >
                      <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                      <path d="M6 9l6 6l6 -6" />
                    </svg>
                    More
                  </>
                )}
              </TagButton>
            </ButtonOutlined>
          </AutoRow>
          <FadeContainer className={isOpen ? 'open' : ''}>
            <PresetOptions>
              <ButtonRadioChecked
                width="100%"
                active={presetPriceRange === 'passive'}
                onClick={() => applyPassiveRange()}
              >
                <AutoColumn gap="sm" justify="flex-start">
                  <ResponsiveText style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    Passive <Tag variant={1}>{presetPercentages['passive']}</Tag>
                  </ResponsiveText>
                  <TYPE.main fontWeight={400} fontSize="12px" textAlign="left">
                    {presetDescriptions['passive']}
                  </TYPE.main>
                </AutoColumn>
              </ButtonRadioChecked>
              <ButtonRadioChecked width="100%" active={presetPriceRange === 'wide'} onClick={() => applyWideRange()}>
                <AutoColumn gap="sm" justify="flex-start">
                  <ResponsiveText style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    Wide <Tag variant={1}>{presetPercentages['wide']}</Tag>
                  </ResponsiveText>
                  <TYPE.main fontWeight={400} fontSize="12px" textAlign="left">
                    {presetDescriptions['wide']}
                  </TYPE.main>
                </AutoColumn>
              </ButtonRadioChecked>
              <ButtonRadioChecked
                width="100%"
                active={presetPriceRange === 'narrow'}
                onClick={() => applyNarrowRange()}
              >
                <AutoColumn gap="sm" justify="flex-start">
                  <ResponsiveText style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    Narrow <Tag variant={1}>{presetPercentages['narrow']}</Tag>
                  </ResponsiveText>
                  <TYPE.main fontWeight={400} fontSize="12px" textAlign="left">
                    {presetDescriptions['narrow']}
                  </TYPE.main>
                </AutoColumn>
              </ButtonRadioChecked>
              <ButtonRadioChecked
                width="100%"
                active={presetPriceRange === 'aggressive'}
                onClick={() => applyAggressiveRange()}
              >
                <AutoColumn gap="sm" justify="flex-start">
                  <ResponsiveText style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    Aggressive <Tag variant={1}>{presetPercentages['aggressive']}</Tag>
                  </ResponsiveText>
                  <TYPE.main fontWeight={400} fontSize="12px" textAlign="left">
                    {presetDescriptions['aggressive']}
                  </TYPE.main>
                </AutoColumn>
              </ButtonRadioChecked>
              <ButtonRadioChecked
                width="100%"
                active={presetPriceRange === 'maxRange'}
                onClick={() => applyMaxPriceRange()}
              >
                <AutoColumn gap="sm" justify="flex-start">
                  <ResponsiveText style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    Max Range
                  </ResponsiveText>
                  <TYPE.main fontWeight={400} fontSize="12px" textAlign="left">
                    {presetDescriptions['maxRange']}
                  </TYPE.main>
                </AutoColumn>
              </ButtonRadioChecked>
            </PresetOptions>
          </FadeContainer>
        </>
      )}

      <RowBetween>
        <StepCounter
          value={leftPrice?.toSignificant(5) ?? ''}
          onUserInput={onLeftRangeInput}
          width="48%"
          decrement={isSorted ? getDecrementLower : getIncrementUpper}
          increment={isSorted ? getIncrementLower : getDecrementUpper}
          feeAmount={feeAmount}
          label={leftPrice ? `${currencyB?.symbol}` : '-'}
          title={'Min Price'}
          tokenA={currencyA?.symbol}
          tokenB={currencyB?.symbol}
          isMaxPriceRange={isMaxPriceRange}
          maxOrMinDecimals={minDecimals}
        />
        <StepCounter
          value={rightPrice?.toSignificant(5) ?? ''}
          onUserInput={onRightRangeInput}
          width="48%"
          decrement={isSorted ? getDecrementUpper : getIncrementLower}
          increment={isSorted ? getIncrementUpper : getDecrementLower}
          feeAmount={feeAmount}
          label={rightPrice ? `${currencyB?.symbol}` : '-'}
          tokenA={currencyA?.symbol}
          tokenB={currencyB?.symbol}
          title={'Max Price'}
          isMaxPriceRange={isMaxPriceRange}
          maxOrMinDecimals={maxDecimals}
        />
      </RowBetween>
    </>
  )
}
